import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { DialogActions, DialogContent, Divider } from '@mui/material';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { DropdownChangeEvent } from 'primereact/dropdown';
import Dialog from 'components/UI/Dialog';
import Text from 'components/UI/Text';
import Button from 'components/UI/Button';
import Select from 'components/UI/Select';
import { theme } from 'components/UI';
import { useTranslation } from 'react-i18next';
import { ActionMode, DialogOnCloseReason } from 'models/shared';
import styled from 'styled-components';
import { useStores } from 'stores/hooks/hooks';
import api from 'Api/ApiMethods';
import { Action, Trigger } from 'models/restTypes';

type TriggerFormatted = {
  type: Trigger['type'];
  whenSet: Trigger | undefined;
  whenCleared: Trigger | undefined;
  trigger: Trigger;
};

enum ActionType {
  WhenSet = 'whenSet',
  WhenCleared = 'whenCleared',
}

interface PlotTriggersDialogProps {
  onHide: () => void;
  visible: boolean;
  title: string;
  mode: ActionMode;
}

const StyledDialogContent = styled(DialogContent)`
  padding: 1rem;
  overflow-y: auto;
  max-height: 70vh;
`;

const PlotTriggersDialog: FC<PlotTriggersDialogProps> = observer(
  ({ visible = false, onHide, title }) => {
    const { resellersStore, rootStore } = useStores();
    const plotId = resellersStore.selectedRowsPlots[0]?.id;
    const growerId = resellersStore.selectedRowsPlots[0]?.growerId;

    const { t } = useTranslation('grower');

    const [triggers, setTriggers] = useState<Trigger[]>([]);
    const formattedTriggers = useMemo(() => {
      const triggerFormattedArray: TriggerFormatted[] = [];

      triggers.forEach((trigger) => {
        const existingEntry = triggerFormattedArray.find(
          (entry) => entry.type.uid === trigger.type.uid,
        );

        const triggerHasAction =
          trigger.actionId !== null &&
          trigger.actionId !== undefined &&
          trigger.actionId !== 0;

        if (existingEntry) {
          existingEntry.whenSet =
            trigger.onActiveAlertState && triggerHasAction
              ? trigger
              : existingEntry.whenSet;

          existingEntry.whenCleared =
            !trigger.onActiveAlertState && triggerHasAction
              ? trigger
              : existingEntry.whenCleared;
        } else {
          triggerFormattedArray.push({
            type: trigger.type,
            whenSet:
              trigger.onActiveAlertState && triggerHasAction
                ? trigger
                : undefined,
            whenCleared:
              !trigger.onActiveAlertState && triggerHasAction
                ? trigger
                : undefined,
            trigger,
          });
        }
      });

      return triggerFormattedArray;
    }, [triggers]);

    console.log('formattedTriggers', formattedTriggers);

    const [actions, setActions] = useState<Action[]>([]);

    console.log('Triggers:', triggers);
    console.log('Actions:', actions);

    const actionOptions = useMemo(
      () => [
        // { label: '—', value: null },
        { label: 'No action', value: 0 },
        ...((actions || []).map((a) => ({ label: a.name, value: a.id })) || []),
      ],
      [actions],
    );

    const loadTriggerSettings = useCallback(
      async (controller: AbortController) => {
        if (!plotId || !growerId) return;
        try {
          const [triggerRes, actionsRes] = await Promise.all([
            api.getTriggers(plotId, controller),
            api.getGrowerActions(growerId, plotId, controller),
          ]);

          if (triggerRes) setTriggers(triggerRes);
          if (actionsRes) setActions(actionsRes.actions);
        } catch (error) {
          console.error('Error fetching trigger settings:', error);
        }
      },
      [plotId, growerId],
    );

    useEffect(() => {
      const controller = new AbortController();
      loadTriggerSettings(controller);
      return () => controller.abort();
    }, [loadTriggerSettings]);

    const handleDeleteAction = async (
      triggerUid: number | null | undefined,
    ) => {
      if (!triggerUid) {
        throw new Error('No trigger UID provided for deletion');
      }

      const res = await api.deleteTrigger(triggerUid);
      if (res) {
        return res;
      }

      return null;
    };

    const handleAddAction = useCallback(
      async (trigger: Trigger) => {
        const res = await api.postTrigger(plotId, trigger);
        return res;
      },
      [plotId],
    );

    // We update the Current TRIGGERS locally instead of updating and waiting for the server with new results
    // Currently , the data from triggers api is very confusing and results in having 2 entries for each select that is not null or 0
    const handleChangeAction = useCallback(
      async (
        row: TriggerFormatted,
        newActionId: number | null,
        type: ActionType,
      ) => {
        try {
          let newTriggerUid = 0;
          const isRemoveAction = newActionId === 0 || newActionId === null;
          if (isRemoveAction) {
            if (
              (type === ActionType.WhenSet && !row?.whenSet?.uid) ||
              (type === ActionType.WhenCleared && !row?.whenCleared?.uid)
            ) {
              throw new Error('No existing trigger to remove for this action');
            }

            await handleDeleteAction(
              type === ActionType.WhenSet ? row.whenSet?.uid : row.whenCleared?.uid,
            );
          } else {
            const newTrigger = await handleAddAction({
              uid: 0, // Assuming 0 for new trigger, real UID should come from server response
              actionId: newActionId,
              plotId: row.trigger.plotId,
              type: row.type,
              onActiveAlertState: type === ActionType.WhenSet,
            });

            newTriggerUid = newTrigger.uid;
          }

          const newTriggers: Trigger[] = [];

          const previousTriggers = triggers.filter(
            (trigger) => trigger.type.uid === row.type.uid,
          ); // Should always exist since trigger exist even without events

          if (!isRemoveAction) {
            const newTrigger: Trigger = {
              actionId: newActionId,
              plotId: row.trigger.plotId,
              type: row.type,
              uid: newTriggerUid,
              onActiveAlertState: type === ActionType.WhenSet,
            };

            const otherTrigger = previousTriggers.find(
              (trigger) =>
                trigger.onActiveAlertState !== newTrigger.onActiveAlertState &&
                trigger.actionId !== null &&
                trigger.actionId !== undefined &&
                trigger.actionId !== 0,
            );

            newTriggers.push(newTrigger);
            if (otherTrigger) {
              const otherCopy = JSON.parse(
                JSON.stringify(otherTrigger),
              ) as Trigger;

              newTriggers.push(otherCopy);
            }
          } else {
            const baseTrigger: Trigger = {
              uid: 0,
              actionId: 0,
              plotId: 0,
              type: { ...row.type },
              onActiveAlertState: false,
            };

            newTriggers.push(
              ...previousTriggers.filter((trigger) =>
                type === ActionType.WhenSet
                  ? trigger.onActiveAlertState !==
                  row.whenSet?.onActiveAlertState
                  : trigger.onActiveAlertState !==
                  row.whenCleared?.onActiveAlertState,
              ),
            );

            if (newTriggers.length === 0) {
              newTriggers.push(baseTrigger);
            }
          }

          const oldTriggers = triggers.filter(
            (trigger) => trigger.type.uid !== row.type.uid,
          );

          const updatedTriggers = [...oldTriggers, ...newTriggers];
          const updatedTriggersSorted = updatedTriggers.sort(
            (a, b) => a.type.uid - b.type.uid,
          );

          setTriggers(updatedTriggersSorted);

          if (!isRemoveAction) {
            rootStore.snackBarStore.showToast({
              detail: 'Trigger action updated successfully',
              severity: 'success',
            });
          } else {
            rootStore.snackBarStore.showToast({
              detail: 'Trigger action removed successfully',
              severity: 'success',
            });
          }
        } catch (error) {
          rootStore.snackBarStore.showToast({
            detail: 'Error updating trigger action',
            severity: 'error',
          });

          console.error('Error in handleChangeAction:', error);
        }
      },
      [handleAddAction, rootStore.snackBarStore, triggers],
    );

    const renderWhenSet = useCallback(
      (row: TriggerFormatted) => {
        return (
          <Select
            value={row.whenSet?.actionId ?? 0}
            options={actionOptions}
            onChange={(e: DropdownChangeEvent) =>
              handleChangeAction(row, e.value, ActionType.WhenSet)
            }
          />
        );
      },
      [actionOptions, handleChangeAction],
    );

    const renderWhenCleared = useCallback(
      (row: TriggerFormatted) => {
        return (
          <Select
            value={row.whenCleared?.actionId ?? 0}
            options={actionOptions}
            onChange={(e: DropdownChangeEvent) =>
              handleChangeAction(row, e.value, ActionType.WhenCleared)
            }
          />
        );
      },
      [actionOptions, handleChangeAction],
    );

    const handleCloseDialog = useCallback(
      (_event: unknown, reason: DialogOnCloseReason) => {
        if (reason === DialogOnCloseReason.BackDropClick) return;
        onHide();
      },
      [onHide],
    );

    const handleClose = useCallback(() => onHide(), [onHide]);

    return (
      <Dialog
        open={visible}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="lg"
        dialogTitle={title}
        titleWeight="bold"
        disableEscapeKeyDown
      >
        <StyledDialogContent>
          <DataTable
            value={formattedTriggers}
            dataKey="type.uid"
            showGridlines
            stripedRows
          >
            <Column
              field="type.type"
              header={t('trigger_type')}
              style={{
                maxWidth: '260px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            />
            <Column field="type.probableCause" header={t('probable_cause')} />
            <Column field="type.description" header={t('description')} />
            <Column header={t('when_set')} body={renderWhenSet} />
            <Column header={t('when_cleared')} body={renderWhenCleared} />
          </DataTable>
        </StyledDialogContent>
        <Divider />
        <DialogActions>
          {/* <Button onClick={handleSave}>
            <Text textcolor={theme.color.white} size="sm">
              {t('save')}
            </Text>
          </Button> */}
          <Button onClick={handleClose}>
            <Text textcolor={theme.color.white} size="sm">
              {t('cancel')}
            </Text>
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

export default PlotTriggersDialog;
